import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CachedIcon from '@mui/icons-material/Cached';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { ReactComponent as CleanAirLevelIcon } from 'assets/images/icons/Clean_Air_Level.svg';
import { ReactComponent as TemperatureIcon } from 'assets/images/icons/temperature.svg';
import GenericModal from 'components/GenericModal';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBuildingDemandSummary } from 'stores/sensor/homePageSlice';
import { brandColors } from 'types/colors';

import Toggle from '../ControllerPage/components/Toggle';
import BuildingDemandModalContent from './BuildingDemandModalContent';

const demandChartOptions = {
  chart: { type: 'donut' },
  labels: ['Justified', 'Saveable', 'Requested'],
  colors: [brandColors.BLUE, brandColors.GREEN, brandColors.RED],
  legend: { show: false },
  dataLabels: { enabled: false },
  stroke: { width: 0 },
  tooltip: { enabled: false },
  states: {
    active: { filter: { type: 'none' } },
    hover: { filter: { type: 'none' } },
  },
  plotOptions: {
    pie: {
      donut: { size: '82%' },
    },
  },
};

const actionButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  px: 2.5,
  py: 1,
  border: `1px solid ${brandColors.BLUE}`,
  color: brandColors.BLUE,
  backgroundColor: '#fff',
  boxShadow: '0 8px 18px rgba(20, 60, 160, 0.25)',
  '&:hover': {
    color: brandColors.BLUE_DARK,
    backgroundColor: brandColors.BLUE_DARK,
    boxShadow: '0 10px 22px rgba(20, 60, 160, 0.35)',
  },
};

const getDemandValue = value => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.round(Math.max(0, Math.min(100, numericValue))) : 0;
};

const formatPercentage = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value;
  }

  return `~${Math.round(numericValue).toFixed(0)}`;
};

const BuildingDemandCard = ({ demandData, selectedTimeRange }) => {
  const dispatch = useDispatch();
  const { distributor, customer, building, floor, area, hvacEquipment } = useSelector(
    state => state.persistence?.selectedFilterObject || {},
  );
  const buildings = useSelector(state => state.users?.buildings || []);
  const [devices, setDevices] = useState(null);
  const [summary, setSummary] = useState(null);
  const [aggregates, setAggregates] = useState({ devices: [], floors: [], areas: [] });
  const [latest, setLatest] = useState({ devices: [], floors: [], areas: [] });
  const [selectedDemand, setSelectedDemand] = useState(null);
  const [demandTimeRange, setDemandTimeRange] = useState(null);
  const [lastDemandFetchAt, setLastDemandFetchAt] = useState(null);
  const [isManuallyRefreshing, setIsManuallyRefreshing] = useState(false);
  const [isManualRefreshCoolingDown, setIsManualRefreshCoolingDown] = useState(false);
  const [isRealTime, setIsRealTime] = useState(false);
  const latestDemandRequestRef = useRef(0);
  const manualRefreshCooldownTimerRef = useRef(null);

  useEffect(
    () => () => {
      clearTimeout(manualRefreshCooldownTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    setDevices(demandData?.aggregates?.devices || null);
    setSummary(demandData?.summary || null);
    setAggregates(demandData?.aggregates || { devices: [], floors: [], areas: [] });
    setLatest(demandData?.latest || { devices: [], floors: [], areas: [] });
    setDemandTimeRange(demandData?.timeRange || null);
    setLastDemandFetchAt(demandData?.fetchedAt || null);
  }, [demandData]);

  const handleManualFetch = useCallback(() => {
    if (!building || building === 'undefined' || building === 'all') {
      return;
    }

    const requestId = latestDemandRequestRef.current + 1;
    latestDemandRequestRef.current = requestId;
    setIsManuallyRefreshing(true);
    setIsManualRefreshCoolingDown(true);
    clearTimeout(manualRefreshCooldownTimerRef.current);
    manualRefreshCooldownTimerRef.current = setTimeout(() => {
      setIsManualRefreshCoolingDown(false);
      manualRefreshCooldownTimerRef.current = null;
    }, 30 * 1000);
    setDemandTimeRange(null);
    dispatch(
      fetchBuildingDemandSummary({
        distributor,
        customer,
        building,
        floor,
        area,
        hvacEquipment,
        time_range: selectedTimeRange,
      }),
    )
      .unwrap()
      .then(result => {
        const cachedResult = {
          devices: result?.aggregates?.devices || [],
          summary: result?.summary || null,
          aggregates: result?.aggregates || { devices: [], floors: [], areas: [] },
          latest: result?.latest || { devices: [], floors: [], areas: [] },
          timeRange: result?.timeRange || null,
          fetchedAt: Date.now(),
        };

        if (latestDemandRequestRef.current === requestId) {
          setDevices(cachedResult.devices);
          setSummary(cachedResult.summary);
          setAggregates(cachedResult.aggregates);
          setLatest(cachedResult.latest);
          setDemandTimeRange(cachedResult.timeRange);
          setLastDemandFetchAt(cachedResult.fetchedAt);
        }
      })
      .catch(error => {
        if (latestDemandRequestRef.current === requestId) {
          console.error('Building demand fetch failed:', error);
          setDevices(null);
          setSummary(null);
          setLatest({ devices: [], floors: [], areas: [] });
        }
      })
      .finally(() => {
        if (latestDemandRequestRef.current === requestId) {
          setIsManuallyRefreshing(false);
        }
      });
  }, [dispatch, distributor, customer, building, floor, area, hvacEquipment, selectedTimeRange]);

  const handleModalClose = () => {
    setSelectedDemand(null);
  };

  const averages = summary;
  const buildingName = useMemo(() => {
    const selectedBuilding = buildings.find(
      candidate => String(candidate.id ?? candidate.building_id ?? candidate.value) === String(building),
    );

    return (
      selectedBuilding?.name ??
      selectedBuilding?.building_name ??
      devices?.[0]?.building_name ??
      devices?.[0]?.building ??
      'Building'
    );
  }, [building, buildings, devices]);
  const comfortDemand = getDemandValue(averages?.thermal_justified);
  const ventilationDemand = getDemandValue(averages?.air_justified);
  const comfortReductionOpportunity = getDemandValue(averages?.thermal_saveable);
  const ventilationReductionOpportunity = getDemandValue(averages?.air_saveable);
  const comfortRequested = getDemandValue(averages?.thermal_requested);
  const ventilationRequested = getDemandValue(averages?.air_requested);

  const openDemandDetails = (demandType, category) => {
    setIsRealTime(false);
    setSelectedDemand({ demandType, category });
  };

  return (
    <Card
      sx={{
        width: { xs: '100%', lg: '24vw' },
        maxWidth: '100%',
        height: { xs: 'auto', lg: '100%' },
        minHeight: 0,
        borderRadius: 3,
        overflow: 'visible',
        border: `2px solid ${brandColors.GREY_LIGHT}`,
        // Use the theme's default Card elevation so it matches the other dashboard cards.
        display: 'flex',
        flexDirection: 'column',
      }}>
      <CardContent
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
        }}>
        {averages ? (
          <Stack
            direction={{ xs: 'column', md: 'row', lg: 'column' }}
            spacing={2}
            justifyContent="center"
            sx={{ width: '100%', height: '100%', overflow: 'visible', px: 0 }}>
            {[
              {
                label: 'Thermal',
                demandType: 'cooling',
                value: comfortDemand,
                reductionOpportunity: comfortReductionOpportunity,
                requested: comfortRequested,
              },
              {
                label: 'Air',
                demandType: 'ventilation',
                value: ventilationDemand,
                reductionOpportunity: ventilationReductionOpportunity,
                requested: ventilationRequested,
              },
            ].map(({ label, demandType, value, reductionOpportunity, requested }) => {
              const DemandIcon = demandType === 'ventilation' ? CleanAirLevelIcon : TemperatureIcon;
              const chartOptions = {
                ...demandChartOptions,
                chart: {
                  ...demandChartOptions.chart,
                  events: {
                    dataPointSelection: (_event, _chartContext, config) => {
                      _event.stopPropagation();
                      openDemandDetails(demandType, ['justified', 'saveable', 'requested'][config.dataPointIndex]);
                    },
                  },
                },
              };

              return (
                <Card
                  className="pie-chart-container"
                  onClick={() => openDemandDetails(demandType)}
                  key={label}
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                    borderRadius: 2,
                    boxShadow: 'none',
                    overflow: 'visible',
                    '&:hover': { cursor: 'pointer' },
                  }}>
                  <Stack
                    className="stackstack"
                    direction="row"
                    alignItems="center"
                    sx={{ px: 0, py: 0, justifyContent: { xs: 'center', lg: 'space-evenly' } }}>
                    <Box sx={{ width: 160, flex: '0 0 160px', overflowY: 'visible' }}>
                      <Stack alignItems="center">
                        <Box sx={{ position: 'relative', width: 160, height: 160, overflowY: 'visible' }}>
                          <Chart
                            type="donut"
                            options={chartOptions}
                            series={[value, reductionOpportunity, requested]}
                            width={160}
                            height={160}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'none',
                            }}>
                            <Stack spacing={0.25} alignItems="center" sx={{ textAlign: 'center', px: 1.5 }}>
                              <DemandIcon style={{ width: 42, height: 42 }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#282828', lineHeight: 1.1 }}>
                                <Box component="span" sx={{ fontSize: '1.5em' }}>
                                  {label}
                                </Box>
                                {label === 'Air' && <br />}
                                {label !== 'Air' && ' '}
                                demand
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>
                      </Stack>
                    </Box>
                    <Stack
                      spacing={1.5}
                      alignItems="flex-start"
                      sx={{ ml: 0.5, mt: 6, pb: 2, whiteSpace: 'nowrap', overflowY: 'visible' }}>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Typography
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={event => {
                            event.stopPropagation();
                            openDemandDetails(demandType, 'justified');
                          }}
                          sx={{
                            border: 0,
                            p: 0,
                            font: 'inherit',
                            fontWeight: 600,
                            color: brandColors.BLUE,
                            background: 'none',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}>
                          {formatPercentage(value)}% Justified
                        </Typography>
                        <Typography
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={event => {
                            event.stopPropagation();
                            openDemandDetails(demandType, 'saveable');
                          }}
                          sx={{
                            border: 0,
                            p: 0,
                            font: 'inherit',
                            fontWeight: 600,
                            color: brandColors.GREEN,
                            background: 'none',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}>
                          {formatPercentage(reductionOpportunity)}% Saveable
                        </Typography>
                        <Typography
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={event => {
                            event.stopPropagation();
                            openDemandDetails(demandType, 'requested');
                          }}
                          sx={{
                            border: 0,
                            p: 0,
                            font: 'inherit',
                            fontWeight: 600,
                            color: brandColors.RED,
                            background: 'none',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}>
                          {formatPercentage(requested)}% Requested
                        </Typography>
                      </Stack>
                      <Button
                        variant="inherit"
                        sx={{ ...actionButtonSx, alignSelf: 'center' }}
                        onClick={event => {
                          event.stopPropagation();
                          openDemandDetails(demandType);
                        }}>
                        Details
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                height: 64,
                width: 64,
                borderRadius: '50%',
                backgroundColor: brandColors.GREY_LIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <ApartmentOutlinedIcon sx={{ fontSize: 34, color: brandColors.BLUE }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#282828' }}>
              Select a building to view Building demand data.
            </Typography>
          </Stack>
        )}
      </CardContent>
      <GenericModal
        open={Boolean(selectedDemand)}
        onClose={handleModalClose}
        paperSx={{
          width: { xs: 'calc(100vw - 32px)', md: 1125 },
          maxWidth: 'calc(100vw - 32px) !important',
          borderRadius: 3,
          overflow: 'hidden',
          '& .MuiDialogTitle-root': {
            bgcolor: '#0d142b',
            p: { xs: 2, sm: 3 },
            pr: { xs: 6, sm: 7 },
            lineHeight: 1,
          },
          '& .MuiDialogTitle-root .MuiIconButton-root': { color: '#dce7fa' },
        }}
        title={
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Typography component="span" sx={{ color: '#fff', fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}>
                  {buildingName}: {selectedDemand?.demandType === 'ventilation' ? 'Air demand' : 'Thermal demand'}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                flexWrap="wrap"
                sx={{ color: '#86a0c9', fontSize: '.78rem', fontWeight: 600 }}>
                <Stack direction="row" spacing={0.65} alignItems="center">
                  <AccessTimeOutlinedIcon sx={{ fontSize: '1rem' }} />
                  <Typography component="span" sx={{ color: 'inherit', font: 'inherit' }}>
                    {demandTimeRange
                      ? `${new Date(demandTimeRange.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} – ${new Date(demandTimeRange.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`
                      : '—'}
                  </Typography>
                </Stack>
                <Typography component="span" sx={{ color: '#52698f' }}>
                  •
                </Typography>
                <Typography component="span" sx={{ color: 'inherit', font: 'inherit' }}>
                  Last fetch{' '}
                  {lastDemandFetchAt
                    ? new Date(lastDemandFetchAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
                    : '—'}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1.25} alignItems="center" flexShrink={0}>
              <Button
                startIcon={<CachedIcon sx={{ scale: 0.5 }} />}
                sx={{
                  textTransform: 'none',
                  color: '#e9f0ff',
                  bgcolor: '#15243b',
                  border: '1px solid #2b3d58',
                  borderRadius: 2,
                  width: 140,
                  minWidth: 140,
                  height: 38,
                  px: 1.5,
                  py: 0,
                  fontSize: '.78rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  '& .MuiButton-startIcon': { mr: -0.5 },
                  '&:hover': { bgcolor: '#203451' },
                  '&.Mui-disabled': { color: '#71809a' },
                }}
                onClick={handleManualFetch}
                disabled={isManuallyRefreshing || isManualRefreshCoolingDown}>
                {isManuallyRefreshing ? 'Refreshing…' : 'Refresh Now'}
              </Button>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{
                  width: 140,
                  height: 38,
                  boxSizing: 'border-box',
                  pl: 1.25,
                  pr: 0.5,
                  border: '1px solid #2b3d58',
                  borderRadius: 2,
                  bgcolor: '#15243b',
                  justifyContent: 'center',
                }}>
                <Typography component="span" sx={{ color: '#dce7fa', fontSize: '.78rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  Real-Time
                </Typography>
                <Toggle
                  checked={isRealTime}
                  handleChange={event => setIsRealTime(event.target.checked)}
                  disabled={!latest?.devices?.length}
                  offContent=""
                  onContent=""
                  inputProps={{ 'aria-label': 'Show real-time values' }}
                  sx={{ width: 'auto', paddingLeft: 0 }}
                />
              </Stack>
            </Stack>
          </Stack>
        }
        contentProps={{ sx: { borderTop: '1px solid #dce3ef' } }}
        Component={BuildingDemandModalContent}
        componentProps={{
          aggregates,
          latest,
          demandType: selectedDemand?.demandType,
          category: selectedDemand?.category,
          isRealTime,
        }}
      />
    </Card>
  );
};

BuildingDemandCard.propTypes = {
  demandData: PropTypes.shape({
    summary: PropTypes.object,
    aggregates: PropTypes.shape({ devices: PropTypes.array, floors: PropTypes.array, areas: PropTypes.array }),
    latest: PropTypes.shape({ devices: PropTypes.array, floors: PropTypes.array, areas: PropTypes.array }),
    timeRange: PropTypes.object,
    fetchedAt: PropTypes.number,
  }),
  selectedTimeRange: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default BuildingDemandCard;
